// =============================================================================
// CLIENT-SIDE IMAGE COMPRESSION
// =============================================================================
// Compresses images in the browser before upload to reduce bandwidth and storage.
// Keeps originals on user's device - server only stores display-quality versions.

export interface CompressionOptions {
  maxWidth: number;      // Max dimension (maintains aspect ratio)
  maxHeight: number;     // Max dimension (maintains aspect ratio)
  quality: number;       // 0-1, e.g., 0.8 = 80%
  outputType: 'image/jpeg' | 'image/webp';
}

export interface CompressionResult {
  blob: Blob;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

const DEFAULT_OPTIONS: CompressionOptions = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  outputType: 'image/jpeg', // JPEG for best compatibility
};

/**
 * Compress a single image file
 */
export async function compressImage(
  file: File,
  options: Partial<CompressionOptions> = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const originalSize = file.size;

  // Load image into an HTMLImageElement
  const img = await loadImage(file);

  // Calculate new dimensions maintaining aspect ratio
  const { width, height } = calculateDimensions(
    img.width,
    img.height,
    opts.maxWidth,
    opts.maxHeight
  );

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw the image
  ctx.drawImage(img, 0, 0, width, height);

  // Convert to blob
  const blob = await canvasToBlob(canvas, opts.outputType, opts.quality);

  const compressedSize = blob.size;
  const compressionRatio = originalSize > 0 ? (1 - compressedSize / originalSize) * 100 : 0;

  return {
    blob,
    width,
    height,
    originalSize,
    compressedSize,
    compressionRatio,
  };
}

/**
 * Compress multiple images with progress callback
 */
export async function compressImages(
  files: File[],
  options: Partial<CompressionOptions> = {},
  onProgress?: (completed: number, total: number, current: string) => void
): Promise<{ file: File; result: CompressionResult }[]> {
  const results: { file: File; result: CompressionResult }[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress?.(i, files.length, file.name);

    try {
      const result = await compressImage(file, options);
      results.push({ file, result });
    } catch {
      // If compression fails, use original file
      results.push({
        file,
        result: {
          blob: file,
          width: 0,
          height: 0,
          originalSize: file.size,
          compressedSize: file.size,
          compressionRatio: 0,
        },
      });
    }
  }

  onProgress?.(files.length, files.length, 'Done');
  return results;
}

/**
 * Convert compressed results to File objects for upload
 */
export function compressionResultsToFiles(
  results: { file: File; result: CompressionResult }[]
): File[] {
  return results.map(({ file, result }) => {
    // Create new File from blob with original name
    const extension = result.blob.type === 'image/webp' ? '.webp' : '.jpg';
    const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');
    const newName = `${nameWithoutExt}${extension}`;

    return new File([result.blob], newName, { type: result.blob.type });
  });
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Get compression summary for UI display
 */
export function getCompressionSummary(
  results: { file: File; result: CompressionResult }[]
): {
  totalOriginal: number;
  totalCompressed: number;
  averageRatio: number;
  formatted: {
    original: string;
    compressed: string;
    saved: string;
    ratio: string;
  };
} {
  const totalOriginal = results.reduce((sum, r) => sum + r.result.originalSize, 0);
  const totalCompressed = results.reduce((sum, r) => sum + r.result.compressedSize, 0);
  const averageRatio = totalOriginal > 0 ? (1 - totalCompressed / totalOriginal) * 100 : 0;

  return {
    totalOriginal,
    totalCompressed,
    averageRatio,
    formatted: {
      original: formatBytes(totalOriginal),
      compressed: formatBytes(totalCompressed),
      saved: formatBytes(totalOriginal - totalCompressed),
      ratio: `${averageRatio.toFixed(0)}%`,
    },
  };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src); // Clean up
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error(`Failed to load image: ${file.name}`));
    };
    img.src = URL.createObjectURL(file);
  });
}

function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Only resize if larger than max dimensions
  if (width > maxWidth || height > maxHeight) {
    const aspectRatio = width / height;

    if (width > height) {
      // Landscape
      width = Math.min(width, maxWidth);
      height = Math.round(width / aspectRatio);
    } else {
      // Portrait or square
      height = Math.min(height, maxHeight);
      width = Math.round(height * aspectRatio);
    }

    // Final check to ensure both dimensions are within limits
    if (width > maxWidth) {
      width = maxWidth;
      height = Math.round(width / aspectRatio);
    }
    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height * aspectRatio);
    }
  }

  return { width, height };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      type,
      quality
    );
  });
}
